import { GridView, KeyboardArrowDown } from "@mui/icons-material";
import { Button, Checkbox, Divider, ListItemText, ListSubheader, MenuItem, OutlinedInput, Popover, Radio, Select, SxProps, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { GridDensity, } from "@mui/x-data-grid";
import { COLORS } from "@styles";
import { OrganizationDetailTableColumns, OrgDetailsFilterProps } from "pages/Organizations/_/OrganizationDetailTable";
import { useState } from "react";

const INPUT_STYLE: SxProps = { border: `1px solid ${COLORS.NEUTRAL_DARK_2}`, borderRadius: "1rem", display: "flex", minWidth: "10rem", width: "fit-content" };
const LIST_SUBHEADER_STYLE: SxProps = { color: COLORS.NEUTRAL_WHITE, backgroundColor: COLORS.NEUTRAL_DARK_2, textTransform: "uppercase", lineHeight: "normal", padding: "0.5rem 1rem", fontSize: "0.625rem" };
const MENU_ITEM_STYLE: SxProps = { padding: "0 1rem", gap: "1rem", };
const MENU_ITEM_TEXT_STYLE: SxProps = { fontSize: "0.75rem", color: COLORS.NEUTRAL_WHITE };

// ===============================================
// OrganizationDetailSingleSelector
// ===============================================
export interface SelectorOptions<T> {
  label: string;
  value: T;
}

interface OrganizationDetailSingleSelectorProps<T> {
  value: T;
  setValue: (value: T) => void;
  options: SelectorOptions<T>[];
  label: string;
}

export function OrganizationDetailSingleSelector<T,>({ label, options, value, setValue }: OrganizationDetailSingleSelectorProps<T>) {
  return <Select
    input={<OutlinedInput sx={INPUT_STYLE} />}
    IconComponent={KeyboardArrowDown}
    MenuProps={{ PaperProps: { sx: { background: COLORS.NEUTRAL_DARK_2 } }, disablePortal: true }}
    size='small'
    variant='outlined'
    displayEmpty
    value={value || ""}
    onChange={(e) => setValue(e.target.value as T)}
    renderValue={() => label}
  >
    {options.map((option) => (
      <MenuItem key={option?.value?.toString()} sx={{ padding: "0 1rem", gap: "1rem" }} value={option?.value?.toString()}>
        <Radio checked={value === option.value} />
        <ListItemText primary={option.label} primaryTypographyProps={{ sx: { fontSize: '0.75rem' } }} />
      </MenuItem>
    ))}
  </Select>
}

const COLLUMNS_OPTIONS: { label: string; value: OrganizationDetailTableColumns }[] = [
  { label: "ID", value: "id" },
  { label: "Email", value: "email" },
  { label: "Work Email", value: "workEmail" },
  { label: "First Name", value: "firstName" },
  { label: "Last Name", value: "lastName" },
  { label: "Phone", value: "phone" },
  { label: "Role", value: "role" },
  { label: "Invitation Status", value: "invitationStatus" },
  { label: "Onboarding Status", value: "onboardingStatus" }
];

const DENSITY_OPTIONS: { label: string; value: GridDensity }[] = [
  { label: "Compact", value: "compact" },
  { label: "Standard", value: "standard" },
  { label: "Comfortable", value: "comfortable" }
];

interface OrganizationDetailColumnsSelectorProps {
  settings: OrgDetailsFilterProps["tableSettings"];
  setSettings: (value: OrgDetailsFilterProps["tableSettings"]) => void;
}

export function OrganizationDetailColumnsSelector({ settings, setSettings }: OrganizationDetailColumnsSelectorProps) {
  const [temporaryValue, setTemporaryValue] = useState<OrgDetailsFilterProps["tableSettings"]>(settings);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const toggleOpen = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);

  const onClose = () => {
    setAnchorEl(null);
    setSettings(temporaryValue);
  };

  const onChange = (optionValue: OrganizationDetailTableColumns) => {
    if (temporaryValue?.columns?.includes(optionValue)) {
      setTemporaryValue((prev: OrgDetailsFilterProps["tableSettings"]) => ({ ...prev, columns: prev.columns?.filter(column => column !== optionValue) }));
      return
    }
    setTemporaryValue((prev: OrgDetailsFilterProps["tableSettings"]) => ({ ...prev, columns: [...prev.columns, optionValue] }));
  }
  return (
    <Box position="relative" display="flex" alignItems="center" justifyContent="center">
      <Button component="button" sx={{ padding: 0, minWidth: 0 }} onClick={(event: React.MouseEvent<HTMLButtonElement>) => toggleOpen(event)}>
        <GridView sx={{ color: "#90CAF9" }} />
      </Button>
      <Popover
        PaperProps={{
          sx: {
            width: "17rem", background: COLORS.NEUTRAL_DARK_2, padding: "1rem", borderRadius: "1rem"
          }
        }}
        open={Boolean(anchorEl)}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }} anchorEl={anchorEl} >
        <ListSubheader sx={{ ...LIST_SUBHEADER_STYLE, padding: "0" }}>Columns</ListSubheader>
        {COLLUMNS_OPTIONS.map(({ label, value: optionValue }) => (
          <MenuItem key={optionValue} sx={{ ...MENU_ITEM_STYLE, padding: "0" }} value={optionValue} onClick={() => onChange(optionValue)}>
            <Checkbox value={optionValue} checked={temporaryValue?.columns?.includes(optionValue) || false} />
            <ListItemText primary={label} primaryTypographyProps={{ sx: MENU_ITEM_TEXT_STYLE }} />
          </MenuItem>
        ))}

        <Divider />

        <Box display='flex' flexDirection='column'>
          <Typography sx={{ marginLeft: "1rem", fontSize: "0.75rem", fontWeight: 600 }}>Density</Typography>
          <Select
            size='medium'
            input={<OutlinedInput sx={{ ...INPUT_STYLE, width: "100%" }} />}
            IconComponent={KeyboardArrowDown}
            renderValue={() => temporaryValue.tableDensity}
            displayEmpty
            value={temporaryValue.tableDensity}
            onChange={(e) => setTemporaryValue((prev: OrgDetailsFilterProps["tableSettings"]) => ({ ...prev, tableDensity: e.target.value as GridDensity }))}
            MenuProps={{ PaperProps: { sx: { background: COLORS.NEUTRAL_DARK_2, width: "17rem" } } }}
          >
            {DENSITY_OPTIONS.map(({ label, value: optionValue }) => (
              <MenuItem key={optionValue} value={optionValue}>{label}</MenuItem>
            ))}
          </Select>
        </Box>
      </Popover >
    </Box >
  )
}